import { create } from 'zustand';
import { AppState, Lawyer, LawFirm, SearchFilters } from '../types';
import { allLawFirms } from '../data/allLawFirms';
import { generateLawyerSeoMetadata } from '../utils/seo';
// Import data files directly - they will be code-split by Vite
import { allLawyersFromJsonParser } from '../data/allLawyersFromJsonParser';
import { allTranslatorsFromJsonParser } from '../data/allTranslatorsFromJsonParser';

const parseExpiryDate = (value?: string) => {
  if (!value) return null;
  const str = String(value).trim();
  if (!str) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  }
  const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) {
    const a = parseInt(m[1], 10);
    const b = parseInt(m[2], 10);
    const year = parseInt(m[3], 10);
    const month = a > 12 ? b : a;
    const day = a > 12 ? a : b;
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  }
  const fallback = new Date(str);
  return isNaN(fallback.getTime()) ? null : fallback;
};

const isLicenseExpired = (licenseStatus?: string, licenseExpiry?: string) => {
  if (licenseStatus && licenseStatus.toLowerCase() === 'expired') return true;
  const exp = parseExpiryDate(licenseExpiry);
  if (!exp) return false;
  const today = new Date();
  return exp < today;
};

interface StoreActions {
  setSearchQuery: (query: string) => void;
  setSearchFilters: (filters: Partial<SearchFilters>) => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setLanguage: (language: 'en' | 'ar') => void;
  setLoading: (loading: boolean) => void;
  setSearchTosAccepted: (accepted: boolean) => void;
  loadLawyersData: () => Promise<void>;
  getLawyerById: (id: string) => Lawyer | undefined;
  getLawyerBySlug: (slug: string) => Lawyer | undefined;
  getFirmById: (id: string) => LawFirm | Lawyer | undefined;
  getFirmBySlug: (slug: string) => LawFirm | Lawyer | undefined;
  getIndividualsByFirmId: (firmId: string) => Lawyer[];
  getFilteredLawyers: () => Lawyer[];
  getFilteredFirms: () => LawFirm[];
}

const initialFilters: SearchFilters = {
  specialization: [],
  emirate: [],
  languages: [],
  verifiedOnly: false,
  category: [],
  showExpired: true,
  expireSoonOnly: false,
};

// Process lawyers data with SEO metadata
// This will be deferred after first paint to prevent blocking
function processLawyersData(): Lawyer[] {
  const allLawyersAndTranslators = [...allLawyersFromJsonParser, ...allTranslatorsFromJsonParser];
  return allLawyersAndTranslators.map(lawyer => {
  const seoMetadata = generateLawyerSeoMetadata(lawyer);
  return {
    ...lawyer,
    ...seoMetadata
  };
});
}

const getInitialSearchTosAccepted = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return localStorage.getItem('uaeLegalDirectorySearchTosAccepted') === 'true';
  } catch {
    return false;
  }
};

export const useStore = create<AppState & StoreActions>((set, get) => ({
  lawyers: [], // Start empty, load async
  lawFirms: allLawFirms,
  searchFilters: initialFilters,
  searchQuery: '',
  searchTosAccepted: getInitialSearchTosAccepted(),
  theme: 'light',
  language: 'en',
  loading: true, // Start with loading true

  setSearchQuery: (query) => set({ searchQuery: query }),
  
  setSearchFilters: (filters) => 
    set((state) => ({ 
      searchFilters: { ...state.searchFilters, ...filters } 
    })),

  setTheme: (theme) => set({ theme }),
  
  setLanguage: (language) => set({ language }),
  
  setLoading: (loading) => set({ loading }),

  setSearchTosAccepted: (accepted) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('uaeLegalDirectorySearchTosAccepted', accepted ? 'true' : 'false');
      } catch {
        // Ignore persistence errors; acceptance will apply for current session.
      }
    }
    set({ searchTosAccepted: accepted });
  },

  loadLawyersData: async () => {
    if (get().lawyers.length > 0) {
      return; // Already loaded
    }
    set({ loading: true });
    try {
      // Defer processing to allow first paint
      const processData = () => {
        const lawyersWithSeo = processLawyersData();
        set({ lawyers: lawyersWithSeo, loading: false });
      };

      // Use requestIdleCallback if available, otherwise setTimeout
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        (window as any).requestIdleCallback(processData, { timeout: 2000 });
      } else {
        setTimeout(processData, 100);
      }
    } catch (error) {
      console.error('Error loading lawyers data:', error);
      set({ loading: false });
    }
  },

  getLawyerById: (id) => get().lawyers.find(lawyer => lawyer.id === id),
  
  getLawyerBySlug: (slug) => get().lawyers.find(lawyer => lawyer.slug === slug),
  
  getFirmById: (id) => {
    // First try the new lawyers array (firms with UUIDs are in there)
    const newFirm = get().lawyers.find(lawyer => 
      lawyer.id === id && 
      (lawyer.category === 'Advocacy_Firms' || 
       lawyer.category === 'Legal_Consultancy_Firms' ||
       lawyer.category === 'Translation_Firms')
    );
    if (newFirm) return newFirm;
    
    // Then try the old lawFirms array as fallback
    return get().lawFirms.find(firm => firm.id === id);
  },
  
  getFirmBySlug: (slug) => {
    // First try the new lawyers array (firms with UUIDs are in there)
    const newFirm = get().lawyers.find(lawyer => 
      lawyer.slug === slug && 
      (lawyer.category === 'Advocacy_Firms' || 
       lawyer.category === 'Legal_Consultancy_Firms' ||
       lawyer.category === 'Translation_Firms')
    );
    if (newFirm) return newFirm;
    
    // Then try the old lawFirms array as fallback
    return get().lawFirms.find(firm => firm.slug === slug);
  },

  getIndividualsByFirmId: (firmId) => get().lawyers.filter(lawyer => lawyer.firmId === firmId),

  getFilteredLawyers: () => {
    const { lawyers, searchFilters, searchQuery } = get();
    const hideExpired = !searchFilters.showExpired;
    const expireSoonOnly = !!searchFilters.expireSoonOnly;
    const now = new Date();
    const soonThreshold = 30; // days

    return lawyers.filter(lawyer => {
      const matchesQuery = !searchQuery ||
        lawyer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lawyer.specializations.some(spec =>
          spec.toLowerCase().includes(searchQuery.toLowerCase())
        );

      const isExpired = isLicenseExpired(lawyer.licenseStatus, lawyer.licenseExpiry);
      if (hideExpired && isExpired) {
        return false;
      }
      if (expireSoonOnly) {
        if (isExpired) return false;
        const exp = parseExpiryDate(lawyer.licenseExpiry);
        if (!exp) return false;
        const diffDays = (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        if (diffDays < 0 || diffDays > soonThreshold) return false;
      }

      const matchesSpecialization = searchFilters.specialization.length === 0 ||
        searchFilters.specialization.some(spec =>
          lawyer.specializations.includes(spec)
        );

      const matchesEmirate = searchFilters.emirate.length === 0 ||
        searchFilters.emirate.includes(lawyer.emirate);

      const matchesLanguages = searchFilters.languages.length === 0 ||
        searchFilters.languages.some(lang => lawyer.languages.includes(lang));

      const matchesVerified = !searchFilters.verifiedOnly || lawyer.verified;

      const matchesCategory = !searchFilters.category || searchFilters.category.length === 0 ||
        (lawyer.category && searchFilters.category.includes(lawyer.category));

      return matchesQuery && matchesSpecialization && matchesEmirate &&
             matchesLanguages && matchesVerified && matchesCategory;
    });
  },

  getFilteredFirms: () => {
    const { lawFirms, searchFilters, searchQuery } = get();
    const hideExpired = !searchFilters.showExpired;
    const expireSoonOnly = !!searchFilters.expireSoonOnly;
    const now = new Date();
    const soonThreshold = 30; // days
    
    return lawFirms.filter(firm => {
      const matchesQuery = !searchQuery || 
        firm.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        firm.specializations.some(spec => 
          spec.toLowerCase().includes(searchQuery.toLowerCase())
        );

      const isExpired = isLicenseExpired(firm.licenseStatus, (firm as any).licenseExpiry);
      if (hideExpired && isExpired) {
        return false;
      }
      if (expireSoonOnly) {
        if (isExpired) return false;
        const exp = parseExpiryDate((firm as any).licenseExpiry);
        if (!exp) return false;
        const diffDays = (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        if (diffDays < 0 || diffDays > soonThreshold) return false;
      }

      const matchesSpecialization = searchFilters.specialization.length === 0 ||
        searchFilters.specialization.some(spec => 
          firm.specializations.includes(spec)
        );

      const matchesEmirate = searchFilters.emirate.length === 0 ||
        searchFilters.emirate.some(emirate => firm.emirates.includes(emirate));

      const matchesVerified = !searchFilters.verifiedOnly || firm.verified;

      return matchesQuery && matchesSpecialization && matchesEmirate && matchesVerified;
    });
  },
}));